import { useMutation } from '@apollo/client';
import AccountContext from 'context/AccountContext';
import { HANDLE_APP_CUSTOMER } from 'graphql/mutations/handleAppCustomer';
import { getLanguage } from 'i18n/init';
import { useContext } from 'react';
import { onboardingCompleted } from 'states/persistInStorage';
import { useAuthStore } from 'stores/authStore';
import {
  Acceptance,
  CountryCode,
  Lang,
  Mutation,
  MutationPatchCustomerArgs,
} from 'types/generatedSchemaTypes';
import { isNewAppUser } from 'utils/accessTokenUtils';
import { isWeb } from 'utils/deviceUtils';
import useAfterAuthenticationRedirect from './useAfterAuthenticationRedirect';

const useHandleCustomerMutation = () => {
  const {
    refetchCustomer: fetchCustomerAfterSignUp,
    customer: { accountType, acceptances },
  } = useContext(AccountContext);
  const { navigateToOnboarding } = useAfterAuthenticationRedirect();
  const [handleAppCustomerMutation, { loading }] = useMutation<
    Mutation,
    MutationPatchCustomerArgs
  >(HANDLE_APP_CUSTOMER, {
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
  });

  const isNewUser = isNewAppUser(
    useAuthStore.getState().accessToken ?? '',
    !!acceptances?.length,
    accountType ?? 'NEW'
  );
  const handleAppCustomer = async (
    marketingConsent: boolean,
    countryCode: CountryCode
  ) => {
    onboardingCompleted(!isNewUser);
    const termsAcceptance: Acceptance = {
      accepted: true,
      type: 'TERMS_ACCEPTED',
      countryCode: countryCode,
    };
    const marketingAcceptance: Acceptance = {
      accepted: marketingConsent,
      type: 'MARKETING_ALLOWED',
      countryCode: countryCode,
    };

    const acceptances = [termsAcceptance, marketingAcceptance];

    await handleAppCustomerMutation({
      variables: {
        input: {
          countryCode: countryCode,
          preferredLanguage: getLanguage() as Lang,
          acceptances,
        },
      },
    });

    await fetchCustomerAfterSignUp();
    if (isWeb) {
      navigateToOnboarding();
    }
  };
  return {
    handleAppCustomer,
    loading,
  };
};
export default useHandleCustomerMutation;
